<template>
  <div style="margin:20px">
    <FiPageRail title="Driver On-Time % Report" />

    <div style="font-size: 22px; font-weight: bold; position: relative; margin: 20px" class="d-flex align-items-center justify-content-center">
      <b-button size="sm" variant="outline-secondary" title="Previous day" v-on:click.prevent="shift_dates(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"></date-picker>
      &nbsp;to&nbsp;
      <date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"></date-picker>&nbsp;
      <b-button size="sm" variant="outline-secondary" title="Next day" v-on:click.prevent="shift_dates(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
      <b-button v-on:click.prevent="load_data()" :disabled="loading">Get Results</b-button>
      &nbsp;&nbsp;
      <b-button size="md" variant="success" :disabled="loading || !on_time_by_driver.length" v-on:click.prevent="download">Download</b-button>
    </div>

    <div style="font-size: 15px; font-weight: bold; margin: 20px; gap: 24px;" class="d-flex align-items-center justify-content-center">
      <span>To Yard within&nbsp;<b-form-select v-model="to_yard_minutes" :options="threshold_options" style="width:75px; display:inline-block;"/>&nbsp;m</span>
      <span>Depart Yard within&nbsp;<b-form-select v-model="depart_yard_minutes" :options="threshold_options" style="width:75px; display:inline-block;"/>&nbsp;m</span>
      <span>To Pickup lead&nbsp;<b-form-select v-model="to_pickup_minutes" :options="threshold_options" style="width:75px; display:inline-block;"/>&nbsp;m</span>
    </div>

    <div v-if="loading">
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="summary.totals">
      <div v-if="summary.totals.with_hos_times === 0" class="pva-empty">
        No successful actuals with both HOS start and finish times found between {{ summary.start_date }} and {{ summary.end_date }}.
      </div>

      <template v-else>
        <div v-if="on_time_metric_tiles.length" class="pva-section">
          <div class="pva-ontime-grid">
            <div
              v-for="tile in on_time_metric_tiles"
              :key="'ontime-' + tile.key"
              class="pva-ontime-tile"
            >
              <div class="pva-ontime-label">{{ tile.label }}</div>
              <div class="pva-ontime-value" :class="on_time_pct_class(tile.on_time_pct)">{{ tile.on_time_pct }}%</div>
              <div class="pva-ontime-meta">{{ tile.on_time_count }} of {{ tile.count }}</div>
              <div class="pva-ontime-rule">{{ tile.rule }}</div>
            </div>
          </div>
        </div>

        <div class="pva-section">
          <div class="pva-section-header">By Driver</div>
          <div v-if="on_time_by_driver.length === 0" class="pva-accuracy-empty">
            Not enough successful movements per driver (need at least 2) to show a breakdown.
          </div>
          <table v-else class="pva-table">
            <tr>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('driver_name')">Driver <i class="fa" :class="on_time_driver_sort_icon('driver_name')"></i></th>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('movements')">Movements <i class="fa" :class="on_time_driver_sort_icon('movements')"></i></th>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('to_yard_pct')">To Yard <i class="fa" :class="on_time_driver_sort_icon('to_yard_pct')"></i></th>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('depart_yard_pct')">Depart Yard <i class="fa" :class="on_time_driver_sort_icon('depart_yard_pct')"></i></th>
              <th class="pva-th-sortable" v-on:click="set_on_time_driver_sort('to_pickup_pct')">To Pickup <i class="fa" :class="on_time_driver_sort_icon('to_pickup_pct')"></i></th>
            </tr>
            <tr v-for="entry in paginated_on_time_by_driver" :key="'ontime-driver-' + entry.driver_name">
              <td><a href="#" v-on:click.prevent="show_driver_detail(entry)">{{ entry.driver_name }}</a></td>
              <td>{{ entry.movements }}</td>
              <td :class="on_time_pct_class(entry.to_yard_pct)">{{ entry.to_yard_pct }}% <span class="pva-more-note">({{ entry.to_yard_on_time_count }}/{{ entry.to_yard_count }})</span></td>
              <td :class="on_time_pct_class(entry.depart_yard_pct)">{{ entry.depart_yard_pct }}% <span class="pva-more-note">({{ entry.depart_yard_on_time_count }}/{{ entry.depart_yard_count }})</span></td>
              <td :class="on_time_pct_class(entry.to_pickup_pct)">{{ entry.to_pickup_pct }}% <span class="pva-more-note">({{ entry.to_pickup_on_time_count }}/{{ entry.to_pickup_count }})</span></td>
            </tr>
          </table>
          <b-pagination
            v-if="sorted_on_time_by_driver.length > driver_per_page"
            v-model="driver_page"
            :total-rows="sorted_on_time_by_driver.length"
            :per-page="driver_per_page"
            align="center"
          ></b-pagination>
        </div>
      </template>
    </div>

    <b-modal
      v-model="show_driver_modal"
      :title="selected_driver_name + ' — Actuals'"
      size="xl"
      dialog-class="ontime-driver-modal-dialog-wide"
      ok-only
      scrollable
    >
      <div v-if="!selected_driver_movements.length" class="pva-accuracy-empty">
        No movement detail available.
      </div>
      <div v-else class="table-responsive">
        <table class="pva-table pva-driver-detail-table">
          <tr>
            <th>Booking</th>
            <th>Vehicle</th>
            <th>Planned Start</th>
            <th>HOS Start</th>
            <th>To Yard &Delta;</th>
            <th>Vehicle Depart</th>
            <th>Depart Yard &Delta;</th>
            <th>Planned Pickup</th>
            <th>Position (Arrival)</th>
            <th>To Pickup &Delta;</th>
          </tr>
          <tr v-for="row in selected_driver_movements" :key="'driver-detail-' + row.booking_hash">
            <td><router-link :to="booking_link(row)">{{ booking_label(row) }}</router-link></td>
            <td>{{ row.vehicle }}</td>
            <td>{{ row.planned_start | formatDateTimeSmall }}</td>
            <td>{{ row.hos_start | formatDateTimeSmall }}</td>
            <td :class="gate_class(row.to_yard_on_time)">{{ format_signed_minutes(row.start_delta_minutes) }}</td>
            <td>{{ row.vehicle_start | formatDateTimeSmall }}</td>
            <td :class="gate_class(row.depart_yard_on_time)">{{ format_signed_minutes(row.vehicle_start_delta_minutes) }}</td>
            <td>{{ row.planned_pickup | formatDateTimeSmall }}</td>
            <td>{{ row.actual_position | formatDateTimeSmall }}</td>
            <td :class="gate_class(row.to_pickup_on_time)">{{ format_signed_minutes(row.to_pickup_delta_minutes) }}</td>
          </tr>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {justDate} from '../helpers/dates'
import moment from 'moment'
import axios from 'axios'
import {api_url, get_actuals_on_time_report} from '../networking/apis'

export default {
  name: 'actuals_on_time_report',

  data() {
    return {
      loading: false,
      summary: {},
      on_time_metrics: {},
      on_time_by_driver: [],
      on_time_driver_sort_col: 'driver_name',
      on_time_driver_sort_dir: 'asc',
      driver_page: 1,
      driver_per_page: 20,
      show_driver_modal: false,
      selected_driver_name: '',
      selected_driver_movements: [],
      initial_load_done: false,
      details_fromDate: justDate(moment().subtract(6, 'days')),
      details_toDate: justDate(moment()),
      to_yard_minutes: 0,
      depart_yard_minutes: 30,
      to_pickup_minutes: 15,
      threshold_options: [0, 5, 10, 15, 20, 30, 45, 60],
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(180, 'days'),
        maxDate: moment().add(1, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    on_time_metric_tiles() {
      const defs = [
        {
          key: 'to_yard',
          label: 'On Time To Yard',
          rule: this.to_yard_minutes > 0
            ? `HOS on duty & assigned to vehicle within ${this.to_yard_minutes}m after planned start`
            : 'HOS on duty & assigned to vehicle at or before planned start',
        },
        {
          key: 'depart_yard',
          label: 'On Time To Depart Yard',
          rule: `Vehicle departure within ${this.depart_yard_minutes}m after planned start`,
        },
        {
          key: 'to_pickup',
          label: 'On Time To Pickup',
          rule: `Arrived at pickup location (position time) at least ${this.to_pickup_minutes}m before planned pickup/departure time`,
        },
      ]
      return defs
        .map(def => ({ ...def, ...(this.on_time_metrics[def.key] || {}) }))
        .filter(tile => tile.count > 0)
    },
    sorted_on_time_by_driver() {
      const col = this.on_time_driver_sort_col
      const dir = this.on_time_driver_sort_dir === 'asc' ? 1 : -1
      const pick = entry => (col === 'driver_name' ? (entry.driver_name || '').toLowerCase() : Number(entry[col] || 0))
      return [...this.on_time_by_driver].sort((a, b) => {
        const av = pick(a), bv = pick(b)
        if (av < bv)
          return -dir
        if (av > bv)
          return dir
        return 0
      })
    },
    paginated_on_time_by_driver() {
      const start = (this.driver_page - 1) * this.driver_per_page
      return this.sorted_on_time_by_driver.slice(start, start + this.driver_per_page)
    },
  },

  async created() {
    if (this.$route.query.fromDate)
      this.details_fromDate = this.$route.query.fromDate
    if (this.$route.query.toDate)
      this.details_toDate = this.$route.query.toDate
    await this.try_initial_load()
  },

  watch: {
    user: {
      deep: true,
      handler() {
        this.try_initial_load()
      },
    },
  },

  methods: {
    async try_initial_load() {
      if (this.initial_load_done || !this.user || !this.user.tenant)
        return
      this.initial_load_done = true
      await this.load_data()
    },
    async load_data() {
      if (!this.user || !this.user.tenant)
        return
      this.loading = true
      try {
        const from = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const to = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const { value: { data = {} } } = await get_actuals_on_time_report(
          this.user.tenant, from, to, undefined, undefined, undefined,
          this.to_yard_minutes, this.depart_yard_minutes, this.to_pickup_minutes,
        )
        this.summary = data.summary || {}
        this.on_time_metrics = data.on_time_metrics || {}
        this.on_time_by_driver = data.on_time_by_driver || []
        this.driver_page = 1
        this.show_driver_modal = false
        if (this.summary.timezone)
          moment.tz.setDefault(this.summary.timezone)
      } catch (e) {
        this.$emit('showAlert', 'error loading data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    shift_dates(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
    },
    async download() {
      this.$emit('showAlert', 'processing download request', 'info', 5000)
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}bookings/reports/actuals/on_time/download`,
          {
            tenant: this.user.tenant,
            start_date: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            end_date: moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            to_yard_minutes: this.to_yard_minutes,
            depart_yard_minutes: this.depart_yard_minutes,
            to_pickup_minutes: this.to_pickup_minutes,
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
            'download',
            `actuals-on-time-${this.details_fromDate}-${this.details_toDate}.csv`
        )
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(() => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'data could not be downloaded.', 'danger')
      })
    },
    booking_label(booking) {
      return booking.booking_id || booking.booking_hash
    },
    booking_link(booking) {
      return { path: '/actuals', query: { booking_id: this.booking_label(booking) } }
    },
    format_minutes(value) {
      if (value == null)
        return '—'
      return `${Math.round(value)}m`
    },
    format_signed_minutes(value) {
      if (value == null)
        return '—'
      const rounded = Math.round(value)
      return `${rounded > 0 ? '+' : ''}${rounded}m`
    },
    on_time_pct_class(pct) {
      if (pct == null)
        return ''
      if (pct >= 90)
        return 'pva-on-time-good'
      if (pct < 70)
        return 'pva-on-time-bad'
      return ''
    },
    set_on_time_driver_sort(col) {
      if (this.on_time_driver_sort_col === col) {
        this.on_time_driver_sort_dir = this.on_time_driver_sort_dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.on_time_driver_sort_col = col
        this.on_time_driver_sort_dir = col === 'driver_name' ? 'asc' : 'desc'
      }
      this.driver_page = 1
    },
    on_time_driver_sort_icon(col) {
      if (this.on_time_driver_sort_col !== col)
        return 'fa-sort pva-th-sort-idle'
      return this.on_time_driver_sort_dir === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc'
    },
    show_driver_detail(entry) {
      this.selected_driver_name = entry.driver_name
      this.selected_driver_movements = entry.movements_detail || []
      this.show_driver_modal = true
    },
    gate_class(passed) {
      if (passed == null)
        return ''
      return passed ? 'pva-on-time-good' : 'pva-on-time-bad'
    },
  },
}
</script>

<style scoped>
.pva-empty {
  font-size: 20px;
  margin: 40px;
  text-align: center;
}
.pva-section {
  margin-bottom: 30px;
  text-align: left;
}
.pva-section-header {
  font-size: 20px;
  font-weight: bold;
  border-bottom: 2px solid #888;
  margin-bottom: 10px;
  padding-bottom: 4px;
  text-align: left;
}
.pva-side-note {
  font-style: italic;
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
}
.pva-more-note {
  font-style: italic;
  font-size: 13px;
  color: #555;
}
.pva-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-bottom: 12px;
}
.pva-table th, .pva-table td {
  border: 1px solid #ddd;
  padding: 4px 8px;
  text-align: left;
}
.pva-table th { background-color: #f5f5f5; }
.pva-th-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.pva-th-sortable:hover { color: #000; }
.pva-th-sortable .fa {
  margin-left: 4px;
  font-size: 11px;
}
.pva-th-sort-idle { color: #bbb; }
.pva-on-time-good { color: #155724; font-weight: bold; }
.pva-on-time-bad { color: #721c24; font-weight: bold; }
.pva-accuracy-empty {
  font-style: italic;
  color: #666;
  font-size: 14px;
}
.pva-ontime-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.pva-ontime-tile {
  border: 1px solid #dee2e6;
  border-left: 4px solid #6c757d;
  border-radius: 8px;
  padding: 12px 14px;
  background: #f8f9fa;
}
.pva-ontime-label {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 6px;
}
.pva-ontime-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
}
.pva-ontime-meta {
  font-size: 12px;
  color: #555;
  margin-top: 4px;
}
.pva-ontime-rule {
  font-size: 12px;
  font-style: italic;
  color: #666;
  margin-top: 6px;
}
.pva-driver-detail-table td,
.pva-driver-detail-table th {
  white-space: nowrap;
}
@media (max-width: 900px) {
  .pva-ontime-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}
</style>

<style>
.ontime-driver-modal-dialog-wide {
  max-width: 1368px !important;
}
</style>
